<template>
  <div class="card border-0 bg-light overflow-hidden">
    <div class="card-body p-4">
      <div class="row g-4 align-items-end">
        <div class="col-12 col-lg">
          <div class="d-flex align-items-start gap-3">
            <div
              class="report-icon d-flex align-items-center justify-content-center rounded-3 bg-white text-primary shadow-sm"
            >
              <FontAwesomeIcon icon="file-invoice-dollar" />
            </div>
            <div>
              <h5 class="mb-1">Monthly service fee report</h5>
              <p class="text-muted mb-0">
                Review and download Soldera service fees grouped by company
                country.
              </p>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-auto">
          <div class="d-flex flex-column flex-sm-row gap-2 align-items-sm-end">
            <div class="flex-grow-1">
              <label
                for="service-fee-report-month"
                class="form-label small fw-semibold mb-1"
              >
                Invoice month
              </label>
              <MonthPickerComponent
                v-model="selectedMonth"
                inputId="service-fee-report-month"
                class="report-month-input"
                :max="currentMonth"
              />
            </div>
            <button
              type="button"
              class="btn btn-primary text-nowrap"
              :disabled="rows.length === 0 || isLoading || isDownloading"
              @click="downloadReport"
            >
              <span
                v-if="isDownloading"
                class="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              <FontAwesomeIcon v-else icon="download" class="me-1" />
              Download CSV
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isLoading" class="border-top bg-white">
      <LoadingIndicatorComponent />
    </div>

    <div
      v-else-if="rows.length === 0"
      class="border-top bg-white py-5 text-center text-muted"
      aria-live="polite"
    >
      <FontAwesomeIcon icon="inbox" class="fs-4 mb-2" />
      <p class="mb-0">No service fees found for the selected month.</p>
    </div>

    <div v-else class="table-responsive border-top bg-white">
      <table class="table table-hover align-middle mb-0">
        <thead class="table-light">
          <tr>
            <th scope="col" class="ps-4">Country</th>
            <th scope="col">Month</th>
            <th scope="col" class="text-end">Items</th>
            <th scope="col" class="text-end">Net</th>
            <th scope="col" class="text-end">VAT</th>
            <th scope="col" class="text-end pe-4">Total incl. VAT</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="`${row.country}-${row.month}`">
            <td class="ps-4 fw-semibold">{{ row.country }}</td>
            <td>{{ row.month }}</td>
            <td class="text-end">{{ row.item_count }}</td>
            <td class="text-end text-nowrap">
              {{ formatAmount(row.total_net) }} €
            </td>
            <td class="text-end text-nowrap">
              {{ formatAmount(row.total_vat) }} €
            </td>
            <td class="text-end text-nowrap pe-4 fw-semibold">
              {{ formatAmount(row.total_with_vat) }} €
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import MonthPickerComponent from "@/components/common/MonthPickerComponent.vue";
import { format, subMonths } from "date-fns";
import { ref, watch } from "vue";

import { goSolidApi } from "@/composables/useGoSolidApi";
import { downloadFromApiResponse } from "@/utils/downloadUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

interface ServiceFeeReportRow {
  country: string;
  month: string;
  item_count: number;
  total_net: number | string;
  total_vat: number | string;
  total_with_vat: number | string;
}

interface ServiceFeeReportResponse {
  rows: ServiceFeeReportRow[];
}

defineOptions({
  name: "MonthlyServiceFeeReport",
});

const currentMonth = format(new Date(), "yyyy-MM");
const selectedMonth = ref(format(subMonths(new Date(), 1), "yyyy-MM"));
const rows = ref<ServiceFeeReportRow[]>([]);
const isLoading = ref(false);
const isDownloading = ref(false);
let previewRequestSequence = 0;

watch(
  selectedMonth,
  async (month) => {
    const requestSequence = ++previewRequestSequence;
    if (!month) {
      rows.value = [];
      isLoading.value = false;
      return;
    }

    isLoading.value = true;
    try {
      const response = await goSolidApi.get<ServiceFeeReportResponse>(
        "/api/money/admin/soldera-commission-fee-country-breakdown",
        { params: { month: `${month}-01` } }
      );
      if (requestSequence !== previewRequestSequence) return;
      rows.value = response.data.rows;
    } catch (error) {
      if (requestSequence !== previewRequestSequence) return;
      rows.value = [];
      globalErrorHandler(error);
    } finally {
      if (requestSequence === previewRequestSequence) {
        isLoading.value = false;
      }
    }
  },
  { immediate: true }
);

const formatAmount = (value: number | string): string => {
  const [integerPart, fractionalPart = ""] = String(value).split(".");
  const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${groupedInteger}.${fractionalPart.padEnd(2, "0")}`;
};

const downloadReport = async (): Promise<void> => {
  if (!selectedMonth.value || rows.value.length === 0 || isDownloading.value)
    return;

  isDownloading.value = true;
  try {
    const response = await goSolidApi.get(
      "/api/money/admin/soldera-commission-fee-country-breakdown/csv",
      {
        params: { month: `${selectedMonth.value}-01` },
        responseType: "blob",
      }
    );
    const [year, month] = selectedMonth.value.split("-");
    downloadFromApiResponse(
      response.data,
      response.headers,
      `soldera_teenustasu_raport_${month}_${year}.csv`,
      "text/csv"
    );
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isDownloading.value = false;
  }
};

const { globalErrorHandler } = useGlobalHandlers();
</script>

<style scoped lang="scss">
.report-icon {
  width: 3rem;
  height: 3rem;
  flex: 0 0 3rem;
}

.report-month-input {
  min-width: 11rem;
}

@media (max-width: 575.98px) {
  .report-month-input {
    width: 100%;
  }
}
</style>
